import React, {
  createContext,
  type DetailedHTMLProps,
  type FormEvent,
  type FormHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from 'react'
import {
  useFormContext,
  type UseFormReturn,
  type FieldValues,
  type UseFormProps,
  FormProvider,
  type SubmitHandler,
  useForm,
} from 'react-hook-form'

type ZionConfig<TFieldValues extends FieldValues = FieldValues> = UseFormProps<TFieldValues>

// Create a context to store the form config (mode, etc.) since react-hook-form 7.71.0+ doesn't pass custom properties
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Context needs to accept any FieldValues type
const ZionFormConfigContext = createContext<ZionConfig<any> | undefined>(undefined)

const useZionFormConfigContext = <TFieldValues extends FieldValues = FieldValues>(): ZionConfig<TFieldValues> => {
  const context = useContext(ZionFormConfigContext)
  if (!context) {
    throw new Error('useZionFormConfigContext must be used within a ZionFormProvider')
  }
  return context
}

export type UseZionFormReturn<TFieldValues extends FieldValues = FieldValues> = UseFormReturn<TFieldValues> & {
  config: ZionConfig<TFieldValues>
}

// Hook to access both react-hook-form context AND our custom config
export function useZionFormContext<TFieldValues extends FieldValues = FieldValues>(): UseZionFormReturn<TFieldValues> {
  const formMethods = useFormContext<TFieldValues>()
  const zionConfig = useZionFormConfigContext<TFieldValues>()

  // Combine react-hook-form methods with our config
  return {
    ...formMethods,
    config: zionConfig,
  }
}

type ZionFormProviderProps<TFieldValues extends FieldValues = FieldValues> = {
  methods: UseZionFormReturn<TFieldValues>
  children: ReactNode
}

// We export ZionFormProvider in the off chance we find somewhere it needs to be used. We don't think it will.
export function ZionFormProvider<TFieldValues extends FieldValues = FieldValues>({
  methods: { config, ...methods },
  children,
}: ZionFormProviderProps<TFieldValues>): JSX.Element {
  return (
    <FormProvider {...methods}>
      <ZionFormConfigContext.Provider value={config}>{children}</ZionFormConfigContext.Provider>
    </FormProvider>
  )
}

type ZionFormElementProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
  'onSubmit'
> & {
  onSubmit?: SubmitHandler<TFieldValues>
  children?: ReactNode
}

function ZionFormElementInner<TFieldValues extends FieldValues = FieldValues>(
  { children, onSubmit, ...props }: ZionFormElementProps<TFieldValues>,
  ref: ForwardedRef<HTMLFormElement>
): JSX.Element {
  const { handleSubmit } = useZionFormContext<TFieldValues>()
  const submitWrapper = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      // When forms are nested in the react tree but not in the dom (e.g. your form opens an overlay with another form in it),
      // React improperly propagates all events including submit up through portals in the react tree. In the HTML DOM the
      // form elements are siblings. In the React tree, they are parent-child. When Return is pressed in the "child" form,
      // we want that form to submit, but we don't want the parent form to submit. Event.stopPropagation() in the submit
      // function prevents the parent form from submitting. We feel confident that the submit event should never bubble up past
      // the html form element that is listening for it.
      event.stopPropagation()
      if (onSubmit) {
        handleSubmit(onSubmit)(event)
      }
    },
    [handleSubmit, onSubmit]
  )
  return (
    <form {...props} ref={ref} onSubmit={submitWrapper}>
      {children}
    </form>
  )
}

// We export ZionFormElement in the off chance we find somewhere it needs to be used. We don't think it will.
export const ZionFormElement = forwardRef(ZionFormElementInner) as <TFieldValues extends FieldValues = FieldValues>(
  props: ZionFormElementProps<TFieldValues> & { ref?: ForwardedRef<HTMLFormElement> }
) => ReturnType<typeof ZionFormElementInner>

/**
 * @description - utility to get values by name out of touchedFields, dirtyFields, errors, getValues(), etc., if they might be names that would turn into an object
 * @param path - the path to lookup in the object (i.e. `'nameTemplate.en.firstName'`, `'searchFields.birthYear'`, `'email'`, etc.)
 * @param object - the object to look into (i.e. results of `getValues()`, `errors`, `touchedFields`, `dirtyFields`, etc.)
 * @returns the value at the nested path of the object, or if that is strictly undefined (not just falsy), the value of the key at the root of the object.
 */

export function getByPath(path: string, object: Record<string, unknown>): unknown {
  // this is copied from stringToPath internal to react-hook-form/dist/utils/stringToPath
  const parts = path
    .replace(/["|']|\]/g, '') // remove quotation marks and closing square brackets
    .split(/\.|\[/) // split on . and [ to get all the pieces
    .filter(Boolean) // remove anything in the path that is falsy

  const target = parts.reduce<unknown>((currentObject, currentKey) => {
    return currentObject && currentObject[currentKey]
  }, object)
  // users have some control over some objects and may not choose to do an unflattened object, such as when they are creating an errors object to return from a validate function. This accounts for that. However, when we have default values that we pass in with the full key rather than as an unflattened object, the value of that does not update when the field updates. Therefore, we only use that value if the target is specifically undefined, meaning that it doesn't exist in the object.
  if (target === undefined && object[path]) return object[path]
  return target
}

type ZionFormProps<TFieldValues extends FieldValues = FieldValues> = {
  children?: ReactNode
  methods: UseZionFormReturn<TFieldValues>
} & Omit<ZionFormElementProps<TFieldValues>, 'children'>

function ZionFormInner<TFieldValues extends FieldValues = FieldValues>(
  { children, methods, ...props }: ZionFormProps<TFieldValues>,
  ref: ForwardedRef<HTMLFormElement>
): JSX.Element {
  return (
    <ZionFormProvider<TFieldValues> methods={methods}>
      <ZionFormElement<TFieldValues> {...props} ref={ref}>
        {children}
      </ZionFormElement>
    </ZionFormProvider>
  )
}

export const ZionForm = forwardRef(ZionFormInner) as <TFieldValues extends FieldValues = FieldValues>(
  props: ZionFormProps<TFieldValues> & { ref?: ForwardedRef<HTMLFormElement> }
) => ReturnType<typeof ZionFormInner>

const formDefaultProps = {
  mode: 'onSubmit',
  reValidateMode: 'onChange',
  shouldUnregister: false,
  defaultValues: undefined,
  resolver: undefined,
  context: undefined,
  criteriaMode: 'firstError',
  shouldFocusError: true,
} as const

/**
 * @description - Zion wrapper for useForm. Under the covers it does the same thing as react-hook-form's useForm but adds a default of shouldUnregister to false.
 * @param options - options provided to the useForm hook from react-hook-form (see their docs: https://react-hook-form.com/v6/api#useForm)
 * @returns the methods and state of the form - the same values that useForm hook from react-hook-form provides (see their docs: https://react-hook-form.com/v6/api#useForm)
 */
export function useZionForm<TFieldValues extends FieldValues = FieldValues>(
  options: UseFormProps<TFieldValues> = {}
): UseZionFormReturn<TFieldValues> {
  /**
   * Decisions made:
   * shouldUnregister is going away in future versions of react-hook-form, so we are defaulting to setting it to false. Most of our use cases shouldn't unregister on unmount (e.g. changing from mobile to desktop dialogs would trigger an unmount/remount and we would lose all the values)
   * We aren't providing any sugar for validation functions - we will all be writing custom resolvers. This will likely be what we need to allow for multiple errors per field and "form-level" errors that are not associated with any particular field
   * If you want to use yup, just import the custom resolver from react-hook-form and set it up like in their docs
   */

  if (process.env.NODE_ENV === 'development' && !options.defaultValues) {
    throw new Error(
      'defaultValues MUST be provided for ALL fields so that form reset and dirty checking function properly.'
    )
  }

  // Use a ref to maintain stable reference across renders, similar to react-hook-form's implementation
  const zionFormReturnRef = useRef<UseZionFormReturn<TFieldValues> | undefined>(undefined)

  const smartDefaultsWithOptions = {
    ...formDefaultProps,
    ...options,
  }

  const methods = useForm<TFieldValues>(smartDefaultsWithOptions)

  // Initialize or update the ref with the current methods and config
  if (!zionFormReturnRef.current) {
    zionFormReturnRef.current = {
      ...methods,
      config: smartDefaultsWithOptions,
    }
  } else {
    // Subsequent renders: update the existing object reference
    // This maintains the same object reference while keeping properties up to date
    Object.assign(zionFormReturnRef.current, { ...methods, config: smartDefaultsWithOptions })
  }

  return zionFormReturnRef.current
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- This is to get the types to show in storybook
export const UseZionFormProps = (_props: UseFormProps<FieldValues>): null => null
